import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { DocumentsExistService } from 'src/app/features/session/services/documents-exist.service';
import { SessionAccessReason } from 'src/app/features/session/models/session-access-reason.enum';
import { SigningStatus } from 'src/app/features/session/models/signing-status.enum';
import { determineAccessReason } from 'src/app/features/session/utils/determine-access-reason';
import { ParticipantSelectionStore } from 'src/app/features/participant-selection/store/participant-selection.store';

export const signingAccessibleGuard: CanActivateFn = async (route) => {
  const platformId = inject(PLATFORM_ID);
  const store = inject(ParticipantSelectionStore);
  const documentsExistService = inject(DocumentsExistService);
  const router = inject(Router);

  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const joinCode = route.paramMap.get('joinCode') ?? '';

  try {
    await store.resolveJoinCode(joinCode);
  } catch {
    return true;
  }

  const metadata = store.signingMetadata();

  let hasDocumentsAndFields = false;
  let isDocumentsCheckUnavailable = false;
  if (metadata) {
    try {
      const documentsExist = await firstValueFrom(
        documentsExistService.getDocumentsExist(metadata.signingId),
      );
      hasDocumentsAndFields = documentsExist.hasDocumentsAndFields;
    } catch {
      isDocumentsCheckUnavailable = true;
    }
  }

  const reason = determineAccessReason({
    status: metadata?.signingStatus as SigningStatus | undefined,
    scheduledDateTime: metadata?.scheduledDateTime,
    capabilities: metadata
      ? {
          isRemote: metadata.capabilities?.isRemote ?? false,
          hasDocumentsAndFields,
          requiresCommonLink: false,
        }
      : null,
    signingJoinCode: joinCode,
  });

  if (reason === SessionAccessReason.Accessible) {
    return true;
  }

  const blockingReason =
    isDocumentsCheckUnavailable && reason === SessionAccessReason.DocumentsNotReady
      ? SessionAccessReason.Unknown
      : reason;

  return router.createUrlTree(['/session-inaccessible'], {
    queryParams: { reason: blockingReason },
  });
};
